import { useState, useEffect, useCallback } from "react";
import DataTable from "react-data-table-component";
import { API_BASE } from "../config";

function formatDuration(seconds) {
  if (!seconds || isNaN(seconds)) return "0:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${s
      .toString()
      .padStart(2, "0")}`;
  } else {
    return `${m}:${s.toString().padStart(2, "0")}`;
  }
}

export default function Logs() {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);

  const [filterText, setFilterText] = useState("");
  const [range, setRange] = useState("week");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchLogs = useCallback(() => {
    let url = `${API_BASE}/api/tracking_log`;
    if (startDate && endDate) {
      url += `?start=${startDate}&end=${endDate}`;
    } else if (range) {
      url += `?range=${range}`;
    }

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        setLogs(data);
        setFilteredLogs(data);
      })
      .catch((err) => console.error("Error fetching logs:", err));
  }, [range, startDate, endDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    const lowerFilter = filterText.toLowerCase();
    const filtered = logs.filter((log) => {
      return (
        log.nama.toLowerCase().includes(lowerFilter) ||
        log.uid.toLowerCase().includes(lowerFilter) ||
        log.departemen.toLowerCase().includes(lowerFilter) ||
        log.lokasi.toLowerCase().includes(lowerFilter)
      );
    });
    setFilteredLogs(filtered);
  }, [filterText, logs]);

  const columns = [
    { name: "User", selector: (row) => row.nama, sortable: true,
    wrap: true,         
    minWidth: "180px",  
   },
    { name: "UID", selector: (row) => row.uid, sortable: true },
    { name: "Departemen", selector: (row) => row.departemen, sortable: true },
    { name: "Lokasi", selector: (row) => row.lokasi, sortable: true },
    { name: "Event", selector: (row) => row.event_type, sortable: true },
    {
      name: "Duration",
      selector: (row) => formatDuration(row.duration),
      sortable: true,
      wrap: true,         
      minWidth: "120px",  
    },
    {
      name: "Status",
      selector: (row) => (row.session_open === 1 ? "Open" : "Closed"),
      sortable: true,
      cell: (row) => (
        <span
          className={`px-2 py-1 rounded-md text-sm font-semibold ${
            row.session_open === 1
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {row.session_open === 1 ? "Open" : "Closed"}
        </span>
      ),
    },
    {
      name: "Note",
      selector: (row) => row.note || "-",
      sortable: false,
      wrap: true,
    },
    {
      name: "Timestamp",
      selector: (row) => row.waktu,
      sortable: true,
      wrap: true,         // biar teks bisa turun ke bawah kalau kepanjangan
      minWidth: "180px",  // jaga lebar minimal kolom
    },
    
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "700",
        color: "#1f2937",
      },
    },
    cells: {
      style: {
        fontSize: "14px",
      },
    },
  };

  const handleDateFilter = () => {
    if (!startDate || !endDate) {
      alert("Pilih tanggal mulai dan akhir!");
      return;
    }
    setRange("");
    fetchLogs();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setRange("week");
  };

  return (
    <div className="p-6">
      <h2 className="text-3xl font-bold mb-4">📜 History Logs</h2>

      <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-3 w-full">
        {/* Search */}
        <div className="flex items-center gap-2 w-full md:w-1/2">
          <input
            type="text"
            placeholder="Cari user / UID / Departemen / Lokasi..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {filterText && (
            <button
              onClick={() => setFilterText("")}
              className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
            >
              Clear
            </button>
          )}
        </div>

        {/* Quick Range + Date Range */}
        <div className="flex items-center gap-2">
          <select
            value={range}
            onChange={(e) => {
              setRange(e.target.value);
              setStartDate("");
              setEndDate("");
            }}
            className="border rounded-lg px-3 py-2"
          >
            <option value="day">Hari ini</option>
            <option value="week">7 Hari Terakhir</option>
            <option value="month">30 Hari Terakhir</option>
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />
          <span>-</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2 border rounded-md"
          />

          <button
            onClick={handleDateFilter}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Filter
          </button>

          <button
            onClick={handleReset}
            className="bg-gray-300 text-black px-3 py-2 rounded-lg hover:bg-gray-400 transition"
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 border">
        <DataTable
          columns={columns}
          data={filteredLogs}
          pagination
          highlightOnHover
          striped
          responsive
          noDataComponent="Tidak ada data tracking"
          customStyles={customStyles}
        />
      </div>
    </div>
  );
}
